import React, { useEffect, useRef, useState } from 'react';
import './item-selector.styles.scss';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';
import ImageContainer from '@/components/SHARED/image-container/image-container.component';
import { membershipRestrictions, testMemberRestrictions } from '@/utils/membershipRestrictions';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import UpgradeRequiredModal from '@/components/SHARED/upgrade-required-modal/upgrade-required-modal.component';
import QuantityInput from '@/components/PLAYER/store/store-components/quantity-input.component';
import ItemSearchBar from '@/components/SHARED/search-bar/item-search-bar.component';
import { properCase } from '@/utils/properCase';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfinity } from '@fortawesome/free-solid-svg-icons';
import { useEffectiveMembership } from '@/utils/useEffectiveMembership';

const ItemSelector = ({
  type,
  formData,
  saveData,
  gameElements,
  gameData,
  gameStats,
  headText,
  inst,
  playerId,
  saveKey
}) => {

  const membership = useEffectiveMembership();
  const screenSize = useSelector(state => state.contentData.screenSize);
  const [availableItems, setAvailableItems] = useState([]);
  const [itemsToDisplay, setItemsToDisplay] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const searchRef = useRef();
  const navigate = useNavigate();

  useEffect(() => {
    if (!gameElements) return;
    let avail = Object.values(gameElements)
      .filter(e => e.type === 'item' && e.status === 'active')
    setAvailableItems(avail);
  }, [gameElements])

  useEffect(() => {
    if (!formData) return;
    if (formData[saveKey] &&
      (
        saveKey === 'opt_earnedItems' ||
        saveKey === 'opt_earnedPrizePacks'
      )
    ) {
      setSelectedItems({ ...formData[saveKey] });
    } else if (formData[saveKey] &&
      (
        saveKey === 'opt_prereqItems' ||
        saveKey === 'opt_prizes' ||
        saveKey === 'opt_itemList'
      )
    ) {
      setSelectedItems([...formData[saveKey]])
    }
  }, [formData])

  function checkMembership(e) {
    const res = testMemberRestrictions({ 'gameData': gameData, 'membership': membership, 'create': true });
    if (res.disabled && res.disabledReason.includes('elements')) {
      const planRest = membershipRestrictions[membership.membership];
      const message = `While using the ${planRest.title}, you are only able to have ${planRest.elementCount} active Game Elements per game.`;
      setShowUpgradeModal(message);
      return;
    }
    navigate(`/manage/${gameData.path}/elements/items/create`);
  }

  function chooseMultipleOption(id) {
    let tempItems = [...selectedItems];
    if (tempItems.includes(id)) {
      tempItems.splice(tempItems.indexOf(id), 1)
    } else {
      tempItems.push(id);
    }
    setSelectedItems([...tempItems]);
    saveData(saveKey, [...tempItems]);
  }

  function changeItemQuantity(e, id) {
    let selectedItemObj = {};
    if (!e) {
      if (formData[saveKey] && formData[saveKey][id]) {
        selectedItemObj = { ...formData[saveKey] };
        delete selectedItemObj[id];
        saveData(saveKey, selectedItemObj, playerId);
      }
      return;
    };
    if (formData[saveKey]) {
      selectedItemObj = { ...formData[saveKey] };
    }
    if (selectedItemObj[id]) {
      let selItem = { ...selectedItemObj[id] };
      selItem.quantity = e;
      selectedItemObj[id] = { ...selItem };
    } else {
      selectedItemObj[id] = {
        'id': id,
        'quantity': e
      }
    }
    saveData(saveKey, selectedItemObj, playerId);
  }

  return (
    <div className={`item-selector ${type === 'distribute' || type === 'prereq' || type === 'prizePacks'
        ? ''
        : 'form-section'
      } ${type === 'prizePacks' || type === 'distribute' || type === 'prereq'
        ? ""
        : "g-list-item"
      } ${screenSize}`}>
      {
        (headText) &&
        <>
          <h3>{headText}<span className='required'>*</span></h3>
          <hr />
        </>
      }
      {
        (inst) &&
        <p>{inst}<span className='required'>*</span></p>
      }
      <div className="g-space-0-5"></div>
      <ItemSearchBar
        allItems={availableItems}
        setItemsToDisplay={setItemsToDisplay}
        itemType={type}
        noTypes={type === 'collection' || type === 'prizePacks'}
      />
      <div className="g-space-0-5"></div>
      <div className={`g-list-item ${type === 'distribute' ? 'distribute-select' : 'multiple-select'}`}>
        <div className='item-list'>
          {
            (loading)
              ?
              <div className='center-col'>
                <ThreeDotsLoader />
              </div>
              :
              (itemsToDisplay.length > 0)
                ?
                itemsToDisplay
                  // .sort((a,b) => a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1)
                  .map(i => {

                    let remainingInventory = Infinity;
                    if (type === 'distribute') {
                      // '_purchased' tags a subset of '_earned' rather than being
                      // a separate bucket, so only '_earned' counts as claimed
                      const totalEarned = gameStats[`${i.id}_earned`] ?? 0;
                      let totalInventory = Infinity;
                      // typeof check, not truthiness — a realworld item drained to
                      // exactly 0 must stay capped, not fall through to Infinity
                      if (typeof i.opt_totalInventory === 'number') {
                        totalInventory = i.opt_totalInventory;
                      } else if (i.opt_limitTotal) {
                        totalInventory = i.opt_limitTotal;
                      }
                      remainingInventory = totalInventory - totalEarned;
                    }

                    return (
                      (type === "distribute")
                        ?
                        <div
                          key={i.id}
                          className={`distribute-item-option ${formData[saveKey] && formData[saveKey][i.id] ? 'active' : ''} ${i.type} ${`type-${type}`}`}>
                          <div className='icon-container'>
                            <ImageContainer
                              src={i.icon}
                              alt={`${i.name} item icon`}
                            />
                          </div>
                          <div className='item-name'>{i.name}</div>
                          <div className='item-type meta'>{properCase(i.opt_itemType)}</div>
                          {
                            (type === 'distribute') &&
                            <div className='item-avail meta'>
                              Avail: <span className={remainingInventory === Infinity ? 'infinity' : remainingInventory < 10 ? 'red' : 'green'}>
                                {remainingInventory === Infinity ? <FontAwesomeIcon icon={faInfinity} /> : remainingInventory}
                              </span>
                            </div>
                          }
                          <div className='item-quantity'>
                            <QuantityInput
                              initialValue={formData[saveKey] && formData[saveKey][i.id] ? formData[saveKey][i.id].quantity : 0}
                              min={0}
                              max={remainingInventory ?? i.opt_limitInd ?? 20}
                              onQuantityChange={e => changeItemQuantity(e, i.id)}
                              disabled={
                                i.opt_itemType === 'realworld' && remainingInventory <= 0
                              }
                            />
                          </div>
                        </div>
                        :
                        <div
                          key={i.id}
                          className={`multiple-select-option ${selectedItems.includes(i.id)
                              ?
                              'active'
                              :
                              ''
                            } ${i.type}`}
                          onClick={() => chooseMultipleOption(i.id)}
                        >
                          <div className='item-info'>
                            <div className='icon-container'>
                              <ImageContainer src={i.icon} alt={`${i.name} item icon`} />
                            </div>
                            <div>
                              {i.name}
                              {
                                (screenSize === 'mobile' || screenSize === 'tablet') &&
                                <div className='meta'>{properCase(i.opt_itemType)}</div>
                              }
                            </div>
                            {
                              (screenSize !== 'mobile' && screenSize !== 'tablet') &&
                              <div className='meta right'>{properCase(i.opt_itemType)}</div>
                            }
                            {
                              type === 'sale' ?
                                <div className='cost'>
                                  <ImageContainer
                                    src='/images/icons/coin.png'
                                    className='coin-icon'
                                  />
                                  {i.opt_marketPrice}
                                </div>
                                :
                                <div></div>
                            }
                          </div>
                        </div>
                    )
                  })
                :
                <span>No Items...</span>
          }
        </div>
      </div>
      <UpgradeRequiredModal
        show={showUpgradeModal}
        cancel={() => setShowUpgradeModal(false)}
        closeButton={true}
        message={showUpgradeModal}
      />
    </div>
  )
}

export default ItemSelector